import { APP_CONSTANTS } from "@core/constants/app-constants";
import { useAsyncOperation } from "@core/hooks/use-async-operation";
import { useI18n } from "@core/i18n/i18n";
import { healthcareTeamManager } from "@core/service-container";
import { RegisteredPtInviteData } from "@domain/healthcare-team/healthcare-team";
import {
	sendAnalyticsEventInviteColleagueError,
	sendAnalyticsEventInviteRegisteredColleague,
} from "@domain/healthcare-team/healthcare-team-analytics";
import { useFormatPatients } from "@domain/patient/hooks/use-patients";
import { Patient } from "@domain/patient/patient";
import { User } from "@domain/user/user";
import styled from "@emotion/styled";
import { Button } from "@ui/components/buttons/button";
import { BaseDialog } from "@ui/components/dialog/base-dialog";
import { ToastContext } from "@ui/components/common/toast-provider";
import { CustomMessageTextarea } from "@ui/pages/patient-details/components/healthcare-team-tab/dialog/components/custom-message-textarea";
import { row, stack } from "@ui/style";
import { isNotEmpty } from "@utils/assert";
import { formatName } from "@utils/string-utils";
import { DialogProps } from "primereact/dialog";
import React, { useContext } from "react";
import { Controller, useForm } from "react-hook-form";

interface InviteFormData {
	message?: string;
}

interface InviteRegisteredPtDialogProps extends DialogProps {
	patient: Patient;
	invitedPt: RegisteredPtInviteData;
	invitingPt: User;
	onGoBack: () => void;
	onInvitationSent: () => void;
}

export const InviteRegisteredPtDialog = ({
	patient,
	invitingPt,
	invitedPt,
	onGoBack,
	onInvitationSent,
	...otherProps
}: InviteRegisteredPtDialogProps): JSX.Element => {
	const { format } = useI18n();
	const { formatPatientName } = useFormatPatients();
	const { showToast } = useContext(ToastContext);

	const { control, handleSubmit } = useForm<InviteFormData>({
		defaultValues: {
			message: format("patientDetails.healthcareTeam.inviteDialog.form.customMessage.defaultValue", {
				patientName: formatPatientName(patient, false, false),
				ptName: formatName(invitingPt),
			}),
		},
	});

	const [submitInvitation, isLoading] = useAsyncOperation(async (data: InviteFormData) => {
		try {
			if (data.message === "") data.message = undefined;
			await healthcareTeamManager.sendInvitation(patient.id, invitedPt.email, data.message);
			sendAnalyticsEventInviteRegisteredColleague(patient, invitedPt, isNotEmpty(data.message));
			showToast(
				format("patientDetails.healthcareTeam.inviteDialog.toastSuccess.title"),
				format("patientDetails.healthcareTeam.inviteDialog.toastSuccess.subtitle"),
				"success",
			);
			onInvitationSent();
		} catch (e) {
			sendAnalyticsEventInviteColleagueError(patient);
			console.error("Failed to send invitation", e);
		}
	}, {});

	return (
		<Dialog
			header={format("patientDetails.healthcareTeam.inviteDialog.header")}
			{...otherProps}
			footer={
				<ButtonContainer>
					<Button label={format("global.dialog.back")} onClick={onGoBack} uiStyle={"secondary_B"} />
					<Button
						loading={isLoading}
						type="submit"
						onClick={handleSubmit(submitInvitation)}
						label={format("patientDetails.healthcareTeam.inviteDialog.form.submitButton")}
					/>
				</ButtonContainer>
			}
		>
			<Form onSubmit={handleSubmit(submitInvitation)}>
				<FormField>
					<label className="p-size-m">
						{format("patientDetails.healthcareTeam.inviteDialog.form.invitedColleague.label")}
					</label>
					<FilledInvitedPt className="p-inputtext p-component p-filled">
						<div className="p-size-s-bold">{formatName(invitedPt)}</div>
						<div>{invitedPt.email}</div>
					</FilledInvitedPt>
				</FormField>
				<FormField>
					<label className="p-size-m">
						{format("patientDetails.healthcareTeam.inviteDialog.form.customMessage.label")}
					</label>
					<Controller
						name="message"
						control={control}
						rules={{ maxLength: APP_CONSTANTS.HEALTH_CARE_INVITE_MAX_CUSTOM_MESSAGE_LENGTH }}
						render={({ field }) => (
							<CustomMessageTextarea
								value={field.value ?? ""}
								onChange={field.onChange}
								maxLength={APP_CONSTANTS.HEALTH_CARE_INVITE_MAX_CUSTOM_MESSAGE_LENGTH}
							/>
						)}
					/>
				</FormField>
			</Form>
		</Dialog>
	);
};

const FilledInvitedPt = styled.div`
	max-width: 382px;
`;

const Form = styled.form`
	${stack("M", "flexstart", "stretch")}
`;

const Dialog = styled(BaseDialog)`
	width: 732px;
`;

const ButtonContainer = styled.div`
	${row(24, "spacebetween")};
`;

const FormField = styled.div`
	${stack("S", "flexstart", "stretch")};
`;
